import { styled, css } from 'next-yak'

import {
  Polymorphic,
  type PolymorphicComponent,
  withPrivatePublicMap,
  type YakComponentOf,
} from '@owl-nest/yak/extra-runtime'

import * as breakpoints from '../constants/breakpoints'
import * as colors from '../constants/colors'
import * as fonts from '../constants/fonts'
import * as typography from '../constants/typography'

export const _baseStyle = css`
  color: ${colors.PRIMARY_GREY_900};
  font-family: ${fonts.PRIMARY_FONT};
  margin: 0;
  padding: 0;
`

type Size = 'XS' | 'S' | 'M' | 'L'
type Type = 'regular' | 'semiBold' | 'light'

/**
 * Base styles for copy
 */
export const styles = {
  XS: {
    regular: css`
      font-size: ${typography.COPY_XS_REGULAR.fontSize};
      line-height: ${typography.COPY_XS_REGULAR.lineHeight};
      font-weight: ${typography.COPY_XS_REGULAR.fontWeight};
    `,
    semiBold: css`
      font-size: ${typography.COPY_XS_SEMIBOLD.fontSize};
      line-height: ${typography.COPY_XS_SEMIBOLD.lineHeight};
      font-weight: ${typography.COPY_XS_SEMIBOLD.fontWeight};
    `,
  },
  S: {
    regular: css`
      font-size: ${typography.COPY_S_REGULAR.fontSize};
      line-height: ${typography.COPY_S_REGULAR.lineHeight};
      font-weight: ${typography.COPY_S_REGULAR.fontWeight};
    `,
    semiBold: css`
      font-size: ${typography.COPY_S_SEMIBOLD.fontSize};
      line-height: ${typography.COPY_S_SEMIBOLD.lineHeight};
      font-weight: ${typography.COPY_S_SEMIBOLD.fontWeight};
    `,
  },
  M: {
    light: css`
      font-size: ${typography.COPY_M_LIGHT.fontSize};
      line-height: ${typography.COPY_M_LIGHT.lineHeight};
      font-weight: ${typography.COPY_M_LIGHT.fontWeight};
    `,
    semiBold: css`
      font-size: ${typography.COPY_M_SEMIBOLD.fontSize};
      line-height: ${typography.COPY_M_SEMIBOLD.lineHeight};
      font-weight: ${typography.COPY_M_SEMIBOLD.fontWeight};
    `,
  },
  L: {
    light: css`
      font-size: ${typography.COPY_L_LIGHT.fontSize};
      line-height: ${typography.COPY_L_LIGHT.lineHeight};
      font-weight: ${typography.COPY_L_LIGHT.fontWeight};
    `,
    semiBold: css`
      font-size: ${typography.COPY_L_SEMIBOLD.fontSize};
      line-height: ${typography.COPY_L_SEMIBOLD.lineHeight};
      font-weight: ${typography.COPY_L_SEMIBOLD.fontWeight};
    `,
  },
} satisfies Record<Size, Partial<Record<Type, any>>>

const LEGAL_ASDESKTOP_VALUES = Object.entries(styles).flatMap(([size, value]) => {
  return Object.keys(value).map((type) => `${size}.${type}`)
})

const LEGAL_ASDESKTOP_VALUE_MESSAGE = LEGAL_ASDESKTOP_VALUES.map((value) => `"${value}"`).join(' ')

type VariantProps = {
  $size?: Size
  $type?: Type
}

export const Base = styled(Polymorphic.p)`
  ${_baseStyle}
` as YakComponentOf<PolymorphicComponent<'p'>>

/**
 * Dynamic copy style based on `Size` and `Type`. This is used for the public
 * `asDekstop` props.
 */
const dynamicCopy = css<VariantProps>`
  ${({ $size, $type }) => {
    // can't use `styles[$size][$type]` because yak can't parse member
    // expression and infer what styles are possible. We need to explicitly
    // return each possible style with nested ifs.
    if ($size === 'XS') {
      if ($type === 'regular') {
        return styles.XS.regular
      }
      if ($type === 'semiBold') {
        return styles.XS.semiBold
      }
    }
    if ($size === 'S') {
      if ($type === 'regular') {
        return styles.S.regular
      }
      if ($type === 'semiBold') {
        return styles.S.semiBold
      }
    }
    if ($size === 'M') {
      if ($type === 'light') {
        return styles.M.light
      }
      if ($type === 'semiBold') {
        return styles.M.semiBold
      }
    }
    if ($size === 'L') {
      if ($type === 'light') {
        return styles.L.light
      }
      if ($type === 'semiBold') {
        return styles.L.semiBold
      }
    }
  }}
`

type BaseResponsiveProps = VariantProps & {
  $breakpoint?: 'mobile-s' | 'mobile-m' | 'mobile-l' | 'tablet' | 'laptop' | 'laptop-l' | 'desktop'
}

const baseResponsiveStyle = css<BaseResponsiveProps>`
  ${({ $breakpoint = 'tablet' }) => {
    if ($breakpoint === 'mobile-s') {
      return css<VariantProps>`
        @media screen and ${breakpoints.MOBILE_S} {
          ${dynamicCopy}
        }
      `
    }
    if ($breakpoint === 'mobile-m') {
      return css<VariantProps>`
        @media screen and ${breakpoints.MOBILE_M} {
          ${dynamicCopy}
        }
      `
    }
    if ($breakpoint === 'mobile-l') {
      return css<VariantProps>`
        @media screen and ${breakpoints.MOBILE_L} {
          ${dynamicCopy}
        }
      `
    }
    if ($breakpoint === 'tablet') {
      return css<VariantProps>`
        @media screen and ${breakpoints.TABLET} {
          ${dynamicCopy}
        }
      `
    }
    if ($breakpoint === 'laptop') {
      return css<VariantProps>`
        @media screen and ${breakpoints.LAPTOP} {
          ${dynamicCopy}
        }
      `
    }
    if ($breakpoint === 'laptop-l') {
      return css<VariantProps>`
        @media screen and ${breakpoints.LAPTOP_L} {
          ${dynamicCopy}
        }
      `
    }
    if ($breakpoint === 'desktop') {
      return css<VariantProps>`
        @media screen and ${breakpoints.DESKTOP} {
          ${dynamicCopy}
        }
      `
    }
  }}
`

export type CopyProps = {
  asDesktop?: string
  breakpoint?: 'mobile-s' | 'mobile-m' | 'mobile-l' | 'tablet' | 'laptop' | 'laptop-l' | 'desktop'
}

const privateXS = {
  regular: styled(Base)<BaseResponsiveProps>`
    ${styles.XS.regular}
    ${baseResponsiveStyle}
  `,
  semiBold: styled(Base)<BaseResponsiveProps>`
    ${styles.XS.semiBold}
    ${baseResponsiveStyle}
  `,
}

const privateS = {
  regular: styled(Base)<BaseResponsiveProps>`
    ${styles.S.regular}
    ${baseResponsiveStyle}
  `,
  semiBold: styled(Base)<BaseResponsiveProps>`
    ${styles.S.semiBold}
    ${baseResponsiveStyle}
  `,
}

const privateM = {
  light: styled(Base)<BaseResponsiveProps>`
    ${styles.M.light}
    ${baseResponsiveStyle}
  `,
  semiBold: styled(Base)<BaseResponsiveProps>`
    ${styles.M.semiBold}
    ${baseResponsiveStyle}
  `,
}

const privateL = {
  light: styled(Base)<BaseResponsiveProps>`
    ${styles.L.light}
    ${baseResponsiveStyle}
  `,
  semiBold: styled(Base)<BaseResponsiveProps>`
    ${styles.L.semiBold}
    ${baseResponsiveStyle}
  `,
}

export const XS = {
  regular: styled(withPrivatePublicMap(privateXS.regular, publicToPrivateMap))`` as YakComponentOf<
    PolymorphicComponent<'p', CopyProps>
  >,
  semiBold: styled(withPrivatePublicMap(privateXS.semiBold, publicToPrivateMap))`` as YakComponentOf<
    PolymorphicComponent<'p', CopyProps>
  >,
}

XS.regular.displayName = 'copy.XS.regular'
XS.semiBold.displayName = 'copy.XS.semiBold'

export const S = {
  regular: styled(withPrivatePublicMap(privateS.regular, publicToPrivateMap))`` as YakComponentOf<
    PolymorphicComponent<'p', CopyProps>
  >,
  semiBold: styled(withPrivatePublicMap(privateS.semiBold, publicToPrivateMap))`` as YakComponentOf<
    PolymorphicComponent<'p', CopyProps>
  >,
}

S.regular.displayName = 'copy.S.regular'
S.semiBold.displayName = 'copy.S.semiBold'

export const M = {
  light: styled(withPrivatePublicMap(privateM.light, publicToPrivateMap))`` as YakComponentOf<
    PolymorphicComponent<'p', CopyProps>
  >,
  semiBold: styled(withPrivatePublicMap(privateM.semiBold, publicToPrivateMap))`` as YakComponentOf<
    PolymorphicComponent<'p', CopyProps>
  >,
}

M.light.displayName = 'copy.M.light'
M.semiBold.displayName = 'copy.M.semiBold'

export const L = {
  light: styled(withPrivatePublicMap(privateL.light, publicToPrivateMap))`` as YakComponentOf<
    PolymorphicComponent<'p', CopyProps>
  >,
  semiBold: styled(withPrivatePublicMap(privateL.semiBold, publicToPrivateMap))`` as YakComponentOf<
    PolymorphicComponent<'p', CopyProps>
  >,
}

L.light.displayName = 'copy.L.light'
L.semiBold.displayName = 'copy.L.semiBold'

function publicToPrivateMap({ asDesktop, breakpoint, ...props }: CopyProps) {
  return {
    ...props,
    ...parseAsDesktop(asDesktop),
    $breakpoint: breakpoint,
  }
}

function parseAsDesktop(asDesktop?: string): VariantProps {
  if (asDesktop === undefined) {
    return {}
  }

  if (!LEGAL_ASDESKTOP_VALUES.includes(asDesktop)) {
    console.warn(`[plume] Invalid asDesktop value: "${asDesktop}". Legal values: ${LEGAL_ASDESKTOP_VALUE_MESSAGE}`)
    return {}
  }

  const [size, type] = asDesktop.split('.')

  return { $size: size as Size, $type: type as Type }
}
